import React from 'react';
import { Box, Typography, List, ListItem, ListItemText, Chip, Divider, Avatar } from '@mui/material';
import { UserCircle } from '@phosphor-icons/react';
import useThemeStyles from '../../../../theme/useThemeStyles';

const WeddingPartySection = ({ weddingParty }) => {
  const styles = useThemeStyles();

  const getRoleLabel = (role, customTitle) => {
    if (customTitle) return customTitle;
    
    const labels = {
      maidOfHonor: 'Maid of Honor',
      bestMan: 'Best Man',
      bridesmaid: 'Bridesmaid',
      groomsman: 'Groomsman',
      flowerGirl: 'Flower Girl',
      ringBearer: 'Ring Bearer',
      father: 'Father',
      mother: 'Mother',
      officiant: 'Officiant',
      usher: 'Usher',
      other: 'Other'
    };
    return labels[role] || role;
  };

  const getRoleIcon = (role) => {
    const icons = {
      maidOfHonor: '👑',
      bestMan: '👔',
      bridesmaid: '💐',
      groomsman: '🎩',
      flowerGirl: '🌸',
      ringBearer: '💍',
      father: '👨',
      mother: '👩',
      officiant: '📖',
      usher: '🎫',
      other: '👤'
    };
    return icons[role] || '👤';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const names = name.split(' ');
    return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Group by role
  const partyByRole = weddingParty?.reduce((acc, member) => {
    const role = member.role || 'other';
    if (!acc[role]) {
      acc[role] = [];
    }
    acc[role].push(member);
    return acc;
  }, {}) || {};

  if (!weddingParty || weddingParty.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <UserCircle size={24} style={{ marginRight: 12, color: '#64748b' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
            Wedding Party
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ ...styles.text.secondary }}>
            No wedding party roles assigned yet
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <UserCircle size={24} style={{ marginRight: 12, color: '#64748b' }} />
        <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
          Wedding Party
        </Typography>
      </Box>

      {Object.keys(partyByRole).map((role, roleIndex) => (
        <Box key={role} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <span style={{ fontSize: '24px', marginRight: 8 }}>{getRoleIcon(role)}</span>
            <Typography variant="h6" sx={{ fontWeight: 600, ...styles.text.primary }}>
              {getRoleLabel(role, partyByRole[role][0]?.customRoleTitle)}
            </Typography>
            <Chip
              label={`${partyByRole[role].length}`}
              size="small"
              sx={{ ml: 1 }}
            />
          </Box>

          <List>
            {partyByRole[role].map((member, index) => (
              <React.Fragment key={member._id || index}>
                <ListItem>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      mr: 2,
                      bgcolor: 'primary.main'
                    }}
                  >
                    {getInitials(member.guest?.name)}
                  </Avatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {member.guest?.name || 'Unknown Guest'}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        {member.guest?.email && (
                          <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                            {member.guest.email}
                          </Typography>
                        )}
                        {member.notes && (
                          <Typography variant="body2" sx={{ ...styles.text.secondary, mt: 0.5 }}>
                            {member.notes}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < partyByRole[role].length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          {roleIndex < Object.keys(partyByRole).length - 1 && <Divider sx={{ my: 3 }} />}
        </Box>
      ))}
    </Box>
  );
};

export default WeddingPartySection;

